import { useMemo, useState, type FormEvent } from 'react';
import {
  broadcastAlert,
  BroadcastAlertDtoTypeEnum,
  BroadcastAlertTargetDtoSubscriptionStatusesEnum,
  BroadcastAlertTargetDtoSubscriptionTiersEnum,
  BroadcastAlertTranslationDtoLocaleEnum,
  type BroadcastAlertInput,
  type BroadcastAlertResponse,
  type BroadcastAlertTranslation,
} from '../lib/api';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { pluralKey } from '../i18n';
import { useLocale } from '../i18n/LocaleProvider';

/**
 * Locales a shop can be configured with — the form must collect copy for ALL of them, because
 * the API rejects a payload that misses one rather than defaulting a whole segment of merchants
 * to the fallback language.
 *
 * Taken from the generated client's enum (ultimately `SUPPORTED_SHOP_LOCALES` in
 * `libs/types/src/lib/shop-locales.ts`), so adding a shop language on the backend adds a tab
 * here after the next `npm run generate:api-client`.
 */
const SUPPORTED_SHOP_LOCALES = Object.values(
  BroadcastAlertTranslationDtoLocaleEnum,
);

type ShopLocale = BroadcastAlertTranslationDtoLocaleEnum;

/**
 * Human labels for the locale tabs. Falls back to the bare code for a locale added later.
 *
 * NOT translated, and not part of the UI catalogue: these name the SHOP's language, which reads
 * the same to an operator working in either interface language. `Deutsch` under an English UI is
 * correct here — it is the language the merchant will receive, not a word about it.
 */
const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
};

// Announcements lead with `info`; the fault-coloured severities come after it.
const ALERT_TYPES = [
  BroadcastAlertDtoTypeEnum.Info,
  BroadcastAlertDtoTypeEnum.Success,
  BroadcastAlertDtoTypeEnum.Warning,
  BroadcastAlertDtoTypeEnum.Error,
];

const SUBSCRIPTION_STATUSES = Object.values(
  BroadcastAlertTargetDtoSubscriptionStatusesEnum,
);

const SUBSCRIPTION_TIERS = Object.values(
  BroadcastAlertTargetDtoSubscriptionTiersEnum,
);

/** Copy the operator is writing, per locale. */
interface LocaleDraft {
  title: string;
  message: string;
  actionLabel: string;
}

type Drafts = Record<string, LocaleDraft>;

interface FieldErrors {
  broadcastKey?: string;
  actionUrl?: string;
  reason?: string;
  shopDomains?: string;
  /** Keyed by locale — a locale missing copy is a hard stop, not a fallback. */
  translations?: Record<string, string>;
}

/**
 * Validates the broadcast key against the same rule the API enforces
 * (`BroadcastAlertDto.broadcastKey`: 3–64 chars, `^[a-z0-9][a-z0-9-]*$`), and returns the reason
 * it failed rather than restating the whole rule.
 *
 * It validates the TRIMMED value because that is what `buildInput` sends — checking the raw
 * value rejected a pasted key carrying a trailing space or newline, with a message listing rules
 * the key already satisfied. Naming the offending characters matters for the same reason: a key
 * copied out of a formatted document can carry an en dash (`–`) that is indistinguishable from a
 * hyphen at this font size.
 *
 * Takes the translator rather than reaching for a hook, so it stays a plain function testable
 * without a React tree.
 */
function validateBroadcastKey(
  value: string,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string | undefined {
  const key = value.trim();
  if (key === '') return t('broadcast.keyRequired');
  if (key.length < 3) return t('broadcast.keyTooShort');
  if (key.length > 64) {
    return t('broadcast.keyTooLong', { length: key.length });
  }

  if (/[A-Z]/.test(key)) {
    return t('broadcast.keyLowercase', { suggestion: key.toLowerCase() });
  }

  const illegal = [...new Set(key.replace(/[a-z0-9-]/g, ''))];
  if (illegal.length > 0) {
    return t('broadcast.keyIllegalChars', { chars: illegal.join(' ') });
  }
  if (key.startsWith('-')) return t('broadcast.keyHyphenStart');
  return undefined;
}

function emptyDrafts(): Drafts {
  return Object.fromEntries(
    SUPPORTED_SHOP_LOCALES.map((locale) => [
      locale,
      { title: '', message: '', actionLabel: '' },
    ]),
  );
}

/**
 * Operator-only form to broadcast a custom system alert to merchants — a new-feature
 * announcement, planned maintenance, anything the app has no shipped translation key for.
 *
 * The copy is written once per supported shop locale; the backend creates one in-app alert per
 * targeted shop in that shop's own configured language (`Shop.locale`). Sending is a two-step
 * flow on purpose: "Preview audience" runs the request with `dryRun: true` (writes nothing) so
 * the operator sees the reach, the per-locale split and the rendered text before committing.
 */
export function BroadcastAlertPage() {
  const { t, tCount, tNode } = useLocale();
  const [broadcastKey, setBroadcastKey] = useState('');
  const [type, setType] = useState<BroadcastAlertDtoTypeEnum>(
    BroadcastAlertDtoTypeEnum.Info,
  );
  const [drafts, setDrafts] = useState<Drafts>(emptyDrafts);
  const [activeLocale, setActiveLocale] = useState<ShopLocale>(
    SUPPORTED_SHOP_LOCALES[0],
  );
  const [actionUrl, setActionUrl] = useState('');
  const [isDismissible, setIsDismissible] = useState(true);
  const [shopDomains, setShopDomains] = useState('');
  const [statuses, setStatuses] = useState<
    BroadcastAlertTargetDtoSubscriptionStatusesEnum[]
  >([]);
  const [tiers, setTiers] = useState<
    BroadcastAlertTargetDtoSubscriptionTiersEnum[]
  >([]);
  const [includeShopsWithoutSubscription, setIncludeShopsWithoutSubscription] =
    useState(false);
  const [reason, setReason] = useState('');

  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  /**
   * Whether validation errors are shown yet. The errors themselves are NOT state: they are
   * derived from the current fields on every render (see `fieldErrors` below), so a field that
   * has been corrected stops being red as it is typed. Holding them in state meant an error
   * raised by one submit stayed on screen no matter what was typed afterwards.
   */
  const [showErrors, setShowErrors] = useState(false);
  const [preview, setPreview] = useState<BroadcastAlertResponse | null>(null);
  /** Serialized request the current preview was produced from — see `previewIsStale`. */
  const [previewedInput, setPreviewedInput] = useState<string | null>(null);
  const [result, setResult] = useState<BroadcastAlertResponse | null>(null);

  /** Domains typed one per line / comma separated, normalized to a list. */
  const parsedDomains = useMemo(
    () =>
      shopDomains
        .split(/[\s,]+/)
        .map((domain) => domain.trim().toLowerCase())
        .filter(Boolean),
    [shopDomains],
  );

  function updateDraft(locale: ShopLocale, patch: Partial<LocaleDraft>) {
    setDrafts((prev) => ({ ...prev, [locale]: { ...prev[locale], ...patch } }));
  }

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value)
      ? list.filter((entry) => entry !== value)
      : [...list, value];
  }

  function validate(): FieldErrors {
    const errs: FieldErrors = {};
    const keyError = validateBroadcastKey(broadcastKey, t);
    if (keyError) errs.broadcastKey = keyError;
    // Trimmed, like the key and for the same reason: `buildInput` sends the trimmed value, so a
    // pasted URL with surrounding whitespace must not be rejected here.
    const trimmedActionUrl = actionUrl.trim();
    if (
      trimmedActionUrl !== '' &&
      !/^(\/[^\s]*|https:\/\/[^\s]+|shopify:\/\/[^\s]+)$/.test(trimmedActionUrl)
    ) {
      errs.actionUrl = t('broadcast.errorLink');
    }
    if (reason.trim().length < 10) {
      errs.reason = t('common.reasonMin');
    }
    if (reason.length > 500) {
      errs.reason = t('common.reasonMax');
    }
    const invalidDomain = parsedDomains.find(
      (domain) => !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(domain),
    );
    if (invalidDomain) {
      errs.shopDomains = t('broadcast.errorShopDomain', {
        domain: invalidDomain,
      });
    }

    const translationErrors: Record<string, string> = {};
    for (const locale of SUPPORTED_SHOP_LOCALES) {
      const draft = drafts[locale];
      if (draft.title.trim().length < 3 || draft.message.trim().length < 3) {
        translationErrors[locale] = t('broadcast.errorCopyRequired');
      } else if (draft.title.length > 120) {
        translationErrors[locale] = t('broadcast.errorTitleTooLong');
      } else if (draft.message.length > 1000) {
        translationErrors[locale] = t('broadcast.errorMessageTooLong');
      }
    }
    if (Object.keys(translationErrors).length > 0) {
      errs.translations = translationErrors;
    }
    return errs;
  }

  /** Every current violation — used to gate a submit. */
  const allErrors = validate();
  /** …and the subset shown, which stays empty until the operator has tried to submit once. */
  const fieldErrors: FieldErrors = showErrors ? allErrors : {};

  function buildInput(dryRun: boolean): BroadcastAlertInput {
    const target: BroadcastAlertInput['target'] = {};
    if (parsedDomains.length > 0) target.shopDomains = parsedDomains;
    if (statuses.length > 0) target.subscriptionStatuses = statuses;
    if (tiers.length > 0) target.subscriptionTiers = tiers;
    if (includeShopsWithoutSubscription) {
      target.includeShopsWithoutSubscription = true;
    }

    const translations: BroadcastAlertTranslation[] =
      SUPPORTED_SHOP_LOCALES.map((locale) => ({
        locale,
        title: drafts[locale].title.trim(),
        message: drafts[locale].message.trim(),
        ...(drafts[locale].actionLabel.trim()
          ? { actionLabel: drafts[locale].actionLabel.trim() }
          : {}),
      }));

    return {
      broadcastKey: broadcastKey.trim(),
      type,
      translations,
      ...(actionUrl.trim() ? { actionUrl: actionUrl.trim() } : {}),
      isDismissible,
      ...(Object.keys(target).length > 0 ? { target } : {}),
      dryRun,
      reason: reason.trim(),
    };
  }

  function readApiError(err: unknown): string {
    const fallback = err instanceof Error ? err.message : t('broadcast.failed');
    const axiosErr = err as {
      response?: { data?: { message?: string | string[] } };
    };
    const apiMessage = axiosErr?.response?.data?.message;
    const detail = Array.isArray(apiMessage)
      ? apiMessage.join('; ')
      : apiMessage;
    return detail ?? fallback;
  }

  async function handlePreview(e: FormEvent) {
    e.preventDefault();
    setError('');
    setResult(null);
    setPreview(null);
    setPreviewedInput(null);
    setShowErrors(true);
    if (Object.keys(allErrors).length > 0) return;

    setLoading(true);
    const input = buildInput(true);
    try {
      setPreview(await broadcastAlert(input));
      setPreviewedInput(JSON.stringify(input));
    } catch (err) {
      setError(readApiError(err));
    } finally {
      setLoading(false);
    }
  }

  async function performBroadcast() {
    setConfirming(false);
    setShowErrors(true);
    if (Object.keys(allErrors).length > 0) {
      setError(t('broadcast.staleOnSend'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await broadcastAlert(buildInput(false));
      setResult(response);
      setPreview(null);
      setPreviewedInput(null);
    } catch (err) {
      setError(readApiError(err));
    } finally {
      setLoading(false);
    }
  }

  const draft = drafts[activeLocale];
  const localeError = fieldErrors.translations?.[activeLocale];

  // The send button is armed by a preview of THIS payload. Comparing the whole request — rather
  // than clearing the preview from each individual setter — means a field added later cannot
  // quietly slip past: editing severity or un-ticking "dismissible" after previewing would
  // otherwise send merchants something nobody looked at.
  const previewIsStale =
    preview === null || previewedInput !== JSON.stringify(buildInput(true));

  return (
    <>
      <PageHead
        eyebrow={t('common.operatorOnly')}
        title={t('broadcast.title')}
        lead={tNode('broadcast.lead', {
          auditLog: <code className="text-gray-900">OperatorAuditLog</code>,
        })}
      />

      <PageBody narrow>
        <div className="card">
          <form onSubmit={handlePreview} className="space-y-5">
            <div>
              <label htmlFor="broadcastKey" className="field-label">
                {t('broadcast.keyLabel')}
              </label>
              <input
                id="broadcastKey"
                type="text"
                value={broadcastKey}
                onChange={(entry) => setBroadcastKey(entry.target.value)}
                // Whitespace a paste dragged in is invisible in a one-line field; drop it as
                // soon as the operator leaves, so what is on screen is what gets sent.
                onBlur={(entry) => setBroadcastKey(entry.target.value.trim())}
                placeholder="datev-export-launch"
                autoComplete="off"
                className="field-input font-mono"
              />
              {fieldErrors.broadcastKey ? (
                <p className="field-error">{fieldErrors.broadcastKey}</p>
              ) : (
                <p className="field-hint">
                  {tNode('broadcast.keyHint', {
                    pattern: <code>broadcast:&lt;key&gt;</code>,
                  })}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="alertType" className="field-label">
                {t('broadcast.severityLabel')}
              </label>
              <select
                id="alertType"
                value={type}
                onChange={(entry) => {
                  // Match the option back to the enum instead of asserting the raw string, so an
                  // enum value renamed by a client regen fails here rather than at the API.
                  const picked = ALERT_TYPES.find(
                    (option) => option === entry.target.value,
                  );
                  if (picked) setType(picked);
                }}
                className="field-input"
              >
                {ALERT_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <p className="field-hint">
                {tNode('broadcast.severityHint', {
                  info: <code>info</code>,
                })}
              </p>
            </div>

            {/* Per-locale copy. Every supported shop language must be filled in — the API
                rejects a partial payload rather than silently shipping a fallback language. */}
            <div>
              <span className="field-label">
                {t('broadcast.perLocaleLabel')}
              </span>
              <div className="tabs w-fit mb-3">
                {SUPPORTED_SHOP_LOCALES.map((locale) => {
                  const filled =
                    drafts[locale].title.trim().length >= 3 &&
                    drafts[locale].message.trim().length >= 3;
                  return (
                    <button
                      key={locale}
                      type="button"
                      onClick={() => setActiveLocale(locale)}
                      className={
                        activeLocale === locale
                          ? 'tab tab-active'
                          : 'tab tab-idle'
                      }
                    >
                      {LOCALE_LABELS[locale] ?? locale}
                      <span
                        className={`text-xs ${
                          filled ? 'text-emerald-600' : 'text-amber-600'
                        }`}
                      >
                        {filled ? '✓' : '•'}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div>
                  <label
                    htmlFor={`title-${activeLocale}`}
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    {t('broadcast.titleField', { locale: activeLocale })}
                  </label>
                  <input
                    id={`title-${activeLocale}`}
                    type="text"
                    value={draft.title}
                    maxLength={120}
                    onChange={(entry) =>
                      updateDraft(activeLocale, { title: entry.target.value })
                    }
                    className="field-input"
                  />
                </div>

                <div>
                  <label
                    htmlFor={`message-${activeLocale}`}
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    {t('broadcast.messageField', { locale: activeLocale })}
                  </label>
                  <textarea
                    id={`message-${activeLocale}`}
                    value={draft.message}
                    rows={4}
                    maxLength={1000}
                    onChange={(entry) =>
                      updateDraft(activeLocale, { message: entry.target.value })
                    }
                    className="field-input"
                  />
                  <p className="field-hint text-right tabular-nums">
                    {draft.message.length}/1000
                  </p>
                </div>

                <div>
                  <label
                    htmlFor={`actionLabel-${activeLocale}`}
                    className="block text-xs font-medium text-gray-600 mb-1"
                  >
                    {t('broadcast.actionLabelField', {
                      locale: activeLocale,
                    })}
                  </label>
                  <input
                    id={`actionLabel-${activeLocale}`}
                    type="text"
                    value={draft.actionLabel}
                    maxLength={60}
                    onChange={(entry) =>
                      updateDraft(activeLocale, {
                        actionLabel: entry.target.value,
                      })
                    }
                    className="field-input"
                  />
                  <p className="field-hint">{t('broadcast.actionLabelHint')}</p>
                </div>

                {localeError && <p className="field-error">{localeError}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="actionUrl" className="field-label">
                {t('broadcast.linkLabel')}
              </label>
              <input
                id="actionUrl"
                type="text"
                value={actionUrl}
                onChange={(entry) => setActionUrl(entry.target.value)}
                placeholder="/app/exports"
                autoComplete="off"
                className="field-input font-mono"
              />
              {fieldErrors.actionUrl ? (
                <p className="field-error">{fieldErrors.actionUrl}</p>
              ) : (
                <p className="field-hint">
                  {tNode('broadcast.linkHint', {
                    path: <code>/app/exports</code>,
                    https: <code>https://</code>,
                    deepLink: <code>shopify://</code>,
                  })}
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isDismissible}
                onChange={(entry) => setIsDismissible(entry.target.checked)}
                className="w-4 h-4 rounded border-gray-300 accent-primary"
              />
              {t('broadcast.dismissible')}
            </label>

            {/* ── Audience ─────────────────────────────────────────────── */}
            <fieldset className="border border-gray-200 rounded-xl p-4 space-y-4">
              <legend className="px-2 text-sm font-medium text-gray-700">
                {t('broadcast.audienceLegend')}
              </legend>
              <p className="text-gray-500 text-xs">
                {t('broadcast.audienceNote')}
              </p>

              <div>
                <label
                  htmlFor="shopDomains"
                  className="block text-xs font-medium text-gray-600 mb-1"
                >
                  {t('broadcast.shopsLabel')}
                </label>
                <textarea
                  id="shopDomains"
                  value={shopDomains}
                  rows={3}
                  onChange={(entry) => setShopDomains(entry.target.value)}
                  placeholder="my-dev-shop.myshopify.com"
                  className="field-input font-mono"
                />
                {fieldErrors.shopDomains ? (
                  <p className="field-error">{fieldErrors.shopDomains}</p>
                ) : (
                  <p className="field-hint">{t('broadcast.shopsHint')}</p>
                )}
              </div>

              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1.5">
                  {t('broadcast.statusLabel')}
                </span>
                <div className="flex flex-wrap gap-2">
                  {SUBSCRIPTION_STATUSES.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() =>
                        setStatuses((prev) => toggle(prev, status))
                      }
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                        statuses.includes(status)
                          ? 'bg-primary-50 border-primary-200 text-primary-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      aria-pressed={statuses.includes(status)}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1.5">
                  {t('broadcast.tierLabel')}
                </span>
                <div className="flex flex-wrap gap-2">
                  {SUBSCRIPTION_TIERS.map((tier) => (
                    <button
                      key={tier}
                      type="button"
                      onClick={() => setTiers((prev) => toggle(prev, tier))}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                        tiers.includes(tier)
                          ? 'bg-primary-50 border-primary-200 text-primary-800'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      aria-pressed={tiers.includes(tier)}
                    >
                      {tier}
                    </button>
                  ))}
                </div>
              </div>

              {(statuses.length > 0 || tiers.length > 0) && (
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={includeShopsWithoutSubscription}
                    onChange={(entry) =>
                      setIncludeShopsWithoutSubscription(entry.target.checked)
                    }
                    className="w-4 h-4 rounded border-gray-300 accent-primary"
                  />
                  {t('broadcast.includeWithoutSubscription')}
                </label>
              )}
            </fieldset>

            <div>
              <label htmlFor="reason" className="field-label">
                {t('broadcast.reasonLabel')}
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(entry) => setReason(entry.target.value)}
                rows={3}
                maxLength={500}
                placeholder={t('broadcast.reasonPlaceholder')}
                className="field-input"
              />
              <div className="flex justify-between items-center gap-4">
                {fieldErrors.reason ? (
                  <p className="field-error">{fieldErrors.reason}</p>
                ) : (
                  <p className="field-hint">{t('broadcast.reasonHint')}</p>
                )}
                <p className="field-hint tabular-nums shrink-0">
                  {reason.length}/500
                </p>
              </div>
            </div>

            {error && <StatusBanner tone="error">{error}</StatusBanner>}

            {preview && <Outcome outcome={preview} />}
            {result && <Outcome outcome={result} />}

            {preview && previewIsStale && (
              <StatusBanner tone="warning">
                {t('broadcast.staleWarning')}
              </StatusBanner>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                disabled={loading}
                className="btn-neutral btn-sm flex-1"
              >
                {loading
                  ? t('broadcast.working')
                  : t('broadcast.previewButton')}
              </button>
              <button
                type="button"
                disabled={
                  loading || previewIsStale || preview?.wouldCreate === 0
                }
                onClick={() => setConfirming(true)}
                className="btn-danger btn-sm flex-1"
                title={previewIsStale ? t('broadcast.staleTitle') : undefined}
              >
                {preview && !previewIsStale
                  ? tCount('broadcast.sendTo', preview.wouldCreate)
                  : t('broadcast.sendButton')}
              </button>
            </div>
          </form>
        </div>
      </PageBody>

      <ConfirmDialog
        open={confirming && preview !== null}
        title={t('broadcast.confirmTitle')}
        confirmLabel={t('broadcast.confirmLabel')}
        onConfirm={performBroadcast}
        onCancel={() => setConfirming(false)}
      >
        {tNode(pluralKey('broadcast.confirmBody', preview?.wouldCreate ?? 0), {
          count: <strong>{preview?.wouldCreate}</strong>,
          key: <code className="text-gray-900">{broadcastKey}</code>,
        })}
      </ConfirmDialog>
    </>
  );
}

/** Renders the counts, per-locale split and rendered samples of a dry run or a real send. */
function Outcome({ outcome }: { outcome: BroadcastAlertResponse }) {
  const { t, tCount, tNode } = useLocale();

  return (
    <StatusBanner tone={outcome.dryRun ? 'info' : 'success'}>
      <p className="font-semibold">
        {outcome.dryRun
          ? tCount('broadcast.outcomePreview', outcome.targeted, {
              would: outcome.wouldCreate,
            })
          : tCount('broadcast.outcomeSent', outcome.created)}
      </p>

      <p className="text-xs opacity-80 mt-1">
        {Object.entries(outcome.byLocale)
          .map(([locale, count]) => `${locale}: ${count}`)
          .join(' · ') || t('broadcast.outcomeNoShops')}
        {outcome.skipped > 0 && (
          <> {t('broadcast.outcomeSkipped', { count: outcome.skipped })}</>
        )}
      </p>

      {outcome.normalizedLocaleShops > 0 && (
        <p className="text-xs text-amber-700 mt-1">
          {tNode(
            pluralKey(
              'broadcast.outcomeNormalized',
              outcome.normalizedLocaleShops,
            ),
            {
              count: outcome.normalizedLocaleShops,
              example: <code>de-DE</code>,
            },
          )}
        </p>
      )}

      {outcome.unmatchedShopDomains.length > 0 && (
        <p className="text-xs text-amber-700 mt-1">
          {t('broadcast.outcomeUnmatched', {
            domains: outcome.unmatchedShopDomains.join(', '),
          })}
        </p>
      )}

      {outcome.sample.length > 0 && (
        <div className="space-y-1.5 pt-2">
          {outcome.sample.map((entry) => (
            <div
              key={entry.locale}
              className="bg-white/70 border border-gray-200 rounded-lg px-3 py-2"
            >
              <p className="text-xs opacity-70">
                {t('broadcast.outcomeSample', {
                  locale: entry.locale,
                  shop: entry.shopDomain,
                })}
              </p>
              <p className="font-medium">{entry.title}</p>
              <p className="text-xs opacity-90">{entry.message}</p>
            </div>
          ))}
        </div>
      )}

      {outcome.auditLogId && (
        <p className="text-xs font-mono opacity-70 mt-1">
          OperatorAuditLog: {outcome.auditLogId}
        </p>
      )}
    </StatusBanner>
  );
}
